import type { FuelType } from "@/types/fareCalculation";

/**
 * Default vehicle profile used for the guest fare-engine call. Guests don't pick a
 * vehicle, so we send a common, representative car so the API can return a fare.
 */
export const GUEST_DEFAULT_VEHICLE = {
  availableSeats: 4,
  carCompany: "Honda",
  carModel: "City",
  fuelType: "Petrol" as FuelType,
};

export interface FareComparisonRates {
  /** Flat pickup/base fare in local currency. */
  baseFare: number;
  /** Per-kilometre rate in local currency. */
  perKm: number;
}

/** Indicative solo city-cab (Uber/Ola) pricing — used only for comparison. */
export const CAB_FARE_RATES: FareComparisonRates = { baseFare: 50, perKm: 18 };
/** Indicative solo auto-rickshaw pricing — used only for comparison. */
export const AUTO_FARE_RATES: FareComparisonRates = { baseFare: 30, perKm: 15 };

/** Estimates a one-way solo fare for a given distance and rate card. */
export function estimateFare(distanceKm: number, rates: FareComparisonRates): number {
  if (!Number.isFinite(distanceKm) || distanceKm <= 0) return 0;
  return Math.round(rates.baseFare + distanceKm * rates.perKm);
}

export interface CommuteSavings {
  /** Absolute amount saved per trip vs. the compared option. */
  amount: number;
  /** Percentage saved (0-100), rounded. */
  percent: number;
}

/** Savings of the Duri per-seat fare against a compared solo fare. */
export function computeSavings(comparedFare: number, duriFare: number): CommuteSavings {
  if (comparedFare <= 0) return { amount: 0, percent: 0 };
  const amount = Math.max(0, Math.round(comparedFare - duriFare));
  const percent = Math.round((amount / comparedFare) * 100);
  return { amount, percent };
}

/** Average tailpipe CO₂ for a typical petrol car (kg per km). */
export const CO2_PER_KM_KG = 0.12;

/** Estimated CO₂ (kg) avoided by sharing instead of driving solo, for `distanceKm`. */
export function estimateCo2SavedKg(distanceKm: number): number {
  if (!Number.isFinite(distanceKm) || distanceKm <= 0) return 0;
  return Math.round(distanceKm * CO2_PER_KM_KG * 10) / 10;
}
